import { reconcileAssemblyDependencies, showAssemblyRemovalNotice } from "helpers/cart-assembly-dependencies";
import { AutoCycleTrack } from "helpers/auto-cycle-track";
class CartDrawer extends window.HTMLElement {
  constructor() {
    super();
    this._optimisticProtection = false;
    this._protectionMutationBound = false;
    this._isReconcilingProtection = false;
    this._protectionReconcilePending = false;
    this._pendingProtectionCart = null;
    this._trustBar = null;
    this._onPageShow = this.onPageShow.bind(this);
    this._onAssemblyCartUpdated = this.onAssemblyCartUpdated.bind(this);
    this._isReconcilingAssembly = false;
    this._protectionOptOutKey = "pp_manual_opt_out";
    this.addEventListener("keyup", (evt) => {
      if (evt.code !== "Escape") return;
      if (this.closeActivePopup()) return;
      this.close();
    });
    this.setHeaderCartIconAccessibility();
    this.addEventListener("click", this.onClick.bind(this));
    this.addEventListener("change", this.onChange.bind(this));
  }
  connectedCallback() {
    window.removeEventListener("pageshow", this._onPageShow);
    window.addEventListener("pageshow", this._onPageShow);
    window.removeEventListener("cart:updated", this._onAssemblyCartUpdated);
    window.addEventListener("cart:updated", this._onAssemblyCartUpdated);
    this.initTrustBar();
    this.initProtectionToggle(null);
    this.initOverlayClose();
    this.reconcileAssemblyDependenciesOnLoad().finally(() => {
      this.bindProtectionMutation();
    });
    this.bindOutsideClick();
  }
  disconnectedCallback() {
    var _a;
    (_a = this._trustBar) == null ? void 0 : _a.destroy();
    window.removeEventListener("pageshow", this._onPageShow);
    window.removeEventListener("cart:updated", this._onAssemblyCartUpdated);
    if (this._onOutsideClick) {
      document.removeEventListener("click", this._onOutsideClick);
    }
  }
  initOverlayClose() {
    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (!overlay) return;
    overlay.addEventListener("click", () => {
      this.close();
    });
  }
  isCheckoutReturn(event) {
    const ref = document.referrer || "";
    return !!((event == null ? void 0 : event.persisted) || ref.includes("/checkout") || ref.includes("/checkouts") || ref.includes("checkout."));
  }
  getVisibleCartCount() {
    const badge = document.querySelector("[data-cart-count-new]");
    const badgeCount = Number.parseInt((badge == null ? void 0 : badge.textContent) || "", 10);
    if (Number.isFinite(badgeCount)) return badgeCount;
    return Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, input) => total + (Number.parseInt(input.value, 10) || 0),
      0
    );
  }
  getMerchandiseItemCount(cart) {
    if (!Array.isArray(cart == null ? void 0 : cart.items)) return 0;
    return cart.items.reduce((count, item) => {
      var _a;
      if ((_a = item == null ? void 0 : item.properties) == null ? void 0 : _a._package_protection) return count;
      return count + Math.max(0, Number(item.quantity) || 0);
    }, 0);
  }
  wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }
  async restoreCartAfterCheckout() {
    const expectedCount = this.getVisibleCartCount();
    const delays = [250, 700, 1400];
    let lastError = null;
    for (let index = 0; index < delays.length; index += 1) {
      if (delays[index] > 0) {
        await this.wait(delays[index]);
      }
      try {
        let state = await window.PackageProtection.getState({ force: true });
        const needsSync = (state == null ? void 0 : state.targetVariantId) && !state.disabled && !this.hasManualProtectionOptOut() && (!state.active || !state.synchronized);
        if (needsSync) {
          state = await window.PackageProtection.onCartMutation(state.cart);
        }
        const restoredCount = this.getMerchandiseItemCount(state == null ? void 0 : state.cart);
        const isLastAttempt = index === delays.length - 1;
        if (Number.isFinite(expectedCount) && restoredCount < expectedCount && !isLastAttempt) {
          continue;
        }
        this.updateProtectionUI(state);
        this.updateProtectionPrice(state);
        await this.refreshCartDrawer({
          preserveOpenState: true,
          cart: (state == null ? void 0 : state.cart) || null,
          reconcileProtection: false
        });
        const renderedCount = this.getVisibleCartCount();
        if (Number.isFinite(expectedCount) && renderedCount < expectedCount && !isLastAttempt) {
          continue;
        }
        return;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError || new Error("Unable to restore cart after checkout return");
  }
  async onPageShow(event) {
    this.setCheckoutDisabled(false);
    this.setCheckoutError();
    if (!this.isCheckoutReturn(event) || !window.PackageProtection) return;
    try {
      await this.restoreCartAfterCheckout();
    } catch (error) {
      console.error("Package protection restore error:", error);
      window.location.reload();
    }
  }
  bindProtectionMutation() {
    if (this._protectionMutationBound) return;
    if (!window.PackageProtection) return;
    this._protectionMutationBound = true;
    window.PackageProtection.onCartMutation().then((state) => {
      if (this._optimisticProtection) return;
      this.updateProtectionUI(state);
      this.updateProtectionPrice(state);
      if (state == null ? void 0 : state.changed) {
        this.refreshCartDrawer({
          preserveOpenState: true,
          cart: state.cart,
          reconcileProtection: false
        }).catch((error) => {
          console.error("Package protection initial refresh error:", error);
        });
      }
    });
  }
  hasManualProtectionOptOut() {
    try {
      return localStorage.getItem(this._protectionOptOutKey) === "1";
    } catch (e) {
      return false;
    }
  }
  setManualProtectionOptOut(value) {
    try {
      if (value) localStorage.setItem(this._protectionOptOutKey, "1");
      else localStorage.removeItem(this._protectionOptOutKey);
    } catch (e) {
    }
  }
  getSectionIdsToRender() {
    return this.getSectionsToRender().map((section) => section.id);
  }
  getSectionRequestPayload() {
    return {
      sections: this.getSectionIdsToRender(),
      sections_url: window.location.pathname
    };
  }
  hasRenderedSections(response) {
    return !!(response && typeof response === "object" && response.sections && typeof response.sections === "object" && response.sections["cart-drawer"]);
  }
  renderCartState(sections, {
    open = true,
    preserveOpenState = false,
    cart = null,
    reconcileProtection = true
  } = {}) {
    if (!this.applySections(sections, { cart, reconcileProtection })) {
      return false;
    }
    this.dispatchCartUpdated(cart);
    const shouldOpen = preserveOpenState ? this.classList.contains("active") : open;
    if (shouldOpen) {
      this.open();
    }
    return true;
  }
  async reconcileProtection(cartSnapshot = null) {
    var _a;
    if (!window.PackageProtection) return;
    if (this._optimisticProtection) return;
    if (this._isReconcilingProtection) {
      this._protectionReconcilePending = true;
      if (window.PackageProtection.isCartSnapshot(cartSnapshot)) {
        this._pendingProtectionCart = cartSnapshot;
      }
      return;
    }
    this._isReconcilingProtection = true;
    try {
      const cart = window.PackageProtection.isCartSnapshot(cartSnapshot) ? cartSnapshot : await window.PackageProtection.getCart({ force: true });
      const realItems = window.PackageProtection.getRealItems(cart);
      const protectionItems = window.PackageProtection.getProtectionItems(cart);
      const protectionDisabled = ((_a = cart.attributes) == null ? void 0 : _a._protection_disabled_by_user) === "true";
      if (realItems.length === 0) {
        if (!protectionItems.length) return;
        const state2 = await window.PackageProtection.sync(cart);
        await this.refreshCartDrawer({
          open: false,
          cart: state2 == null ? void 0 : state2.cart,
          reconcileProtection: false
        });
        return;
      }
      if (protectionItems.length > 0 || protectionDisabled || this.hasManualProtectionOptOut()) return;
      const state = await window.PackageProtection.enable(cart);
      this.updateProtectionUI(state);
      this.updateProtectionPrice(state);
      await this.refreshCartDrawer({
        preserveOpenState: true,
        cart: state == null ? void 0 : state.cart,
        reconcileProtection: false
      });
    } catch (error) {
      console.error("Package protection reconciliation error:", error);
    } finally {
      this._isReconcilingProtection = false;
      if (this._protectionReconcilePending) {
        const pendingCart = this._pendingProtectionCart;
        this._protectionReconcilePending = false;
        this._pendingProtectionCart = null;
        queueMicrotask(() => this.reconcileProtection(pendingCart));
      }
    }
  }
  /* -------------------------------- */
  /* QTY + REMOVE */
  /* -------------------------------- */
  async onClick(e) {
    const trustDirection = e.target.closest("[data-trust-prev]") ? -1 : e.target.closest("[data-trust-next]") ? 1 : 0;
    if (trustDirection) {
      this.stepTrustBar(trustDirection);
      return;
    }
    const checkoutWithProtection = e.target.closest(
      "[data-checkout-with-protection]"
    );
    if (checkoutWithProtection) {
      await this.checkoutWithProtection();
      return;
    }
    const checkoutWithoutProtection = e.target.closest(
      "[data-checkout-without-protection]"
    );
    if (checkoutWithoutProtection) {
      await this.checkoutWithoutProtection();
      return;
    }
    const popupTrigger = e.target.closest("[data-popup-target]");
    if (popupTrigger) {
      e.preventDefault();
      this.openPopup(popupTrigger.getAttribute("data-popup-target"));
      return;
    }
    const popupClose = e.target.closest(".custom-close-icon");
    if (popupClose) {
      e.preventDefault();
      const popup = popupClose.closest(".custom-popup");
      if (popup) this.closePopup(popup);
      return;
    }
    if (e.target.classList.contains("custom-popup")) {
      this.closePopup(e.target);
      return;
    }
    const qtyBtn = e.target.closest("quantity-input button");
    if (qtyBtn) {
      const input = qtyBtn.parentElement.querySelector(
        'input[name="updates[]"]'
      );
      if (!input) return;
      const line = Number(input.dataset.index);
      const identifier = input.dataset.key || line;
      const current = parseInt(input.value || "0", 10);
      if (!Number.isFinite(current)) return;
      const next = qtyBtn.name === "plus" ? current + 1 : Math.max(current - 1, 0);
      if (next === current) return;
      this.setQtyButtonLoading(qtyBtn, true);
      this.setCheckoutDisabled(true);
      input.value = String(next);
      try {
        await this.updateCartLine(identifier, next, line);
      } finally {
        this.setQtyButtonLoading(qtyBtn, false);
        this.setCheckoutDisabled(false);
      }
      return;
    }
    const removeBtn = e.target.closest("cart-remove-button button");
    if (removeBtn) {
      const wrap = removeBtn.closest("cart-remove-button");
      const line = Number((wrap == null ? void 0 : wrap.dataset.index) || 0);
      const identifier = (wrap == null ? void 0 : wrap.dataset.key) || line;
      if (!identifier) return;
      await this.updateCartLine(identifier, 0, line);
    }
  }
  setCheckoutError(message = "") {
    const error = this.querySelector("[data-checkout-error]");
    if (!error) return;
    error.textContent = message;
    error.classList.toggle("hidden", !message);
  }
  setCartError(message = "") {
    const error = this.querySelector("#CartDrawer-CartErrors");
    if (error) error.textContent = message;
  }
  initTrustBar() {
    var _a, _b;
    const previousIndex = ((_a = this._trustBar) == null ? void 0 : _a.index) || 0;
    (_b = this._trustBar) == null ? void 0 : _b.destroy();
    this._trustBar = null;
    const bar = this.querySelector("[data-trust-bar]");
    const track = this.querySelector("[data-trust-track]");
    if (!bar || !track) return;
    this._trustBar = new AutoCycleTrack({
      bar,
      track,
      itemSelector: "[data-trust-item]",
      isActive: () => this.classList.contains("active") && window.innerWidth < 640
    });
    this._trustBar.init(previousIndex);
  }
  startTrustBarAutoplay() {
    var _a;
    (_a = this._trustBar) == null ? void 0 : _a.start();
  }
  stopTrustBarAutoplay() {
    var _a;
    (_a = this._trustBar) == null ? void 0 : _a.stop();
  }
  stepTrustBar(direction) {
    var _a;
    (_a = this._trustBar) == null ? void 0 : _a.step(direction);
  }
  goToCheckout() {
    window.location.assign("/checkout");
  }
  async checkoutWithProtection() {
    if (!window.PackageProtection) {
      this.setCheckoutError(
        "Package protection is temporarily unavailable. Please try again."
      );
      return;
    }
    this.setCheckoutError();
    this.setCheckoutDisabled(true);
    try {
      this.setManualProtectionOptOut(false);
      const confirmedState = await window.PackageProtection.enable();
      if (!(confirmedState == null ? void 0 : confirmedState.active) || confirmedState.synchronized !== true) {
        throw new Error("Package protection could not be synchronized");
      }
      this.goToCheckout();
    } catch (error) {
      console.error("Checkout with protection error:", error);
      this.setCheckoutError(
        "We couldn’t add package protection. Please try again."
      );
      this.setCheckoutDisabled(false);
    }
  }
  async checkoutWithoutProtection() {
    this.setCheckoutError();
    this.setCheckoutDisabled(true);
    try {
      this.setManualProtectionOptOut(true);
      if (window.PackageProtection) {
        const state = await window.PackageProtection.disable();
        const protectionStillInCart = (state == null ? void 0 : state.cart) && window.PackageProtection.getProtectionItems(state.cart).length > 0;
        if ((state == null ? void 0 : state.active) || !(state == null ? void 0 : state.disabled) || protectionStillInCart) {
          throw new Error("Package protection could not be removed");
        }
      }
      this.goToCheckout();
    } catch (error) {
      console.error("Checkout without protection error:", error);
      this.setCheckoutError(
        "We couldn’t remove package protection. Please try again."
      );
      this.setCheckoutDisabled(false);
    }
  }
  async onChange(e) {
    const input = e.target.closest('quantity-input input[name="updates[]"]');
    if (!input) return;
    const line = Number(input.dataset.index);
    const identifier = input.dataset.key || line;
    const parsedQuantity = parseInt(input.value, 10);
    if (!Number.isFinite(parsedQuantity)) {
      input.value = input.defaultValue || "1";
      this.setCartError("Enter a valid quantity.");
      return;
    }
    const next = Math.max(parsedQuantity, 0);
    await this.updateCartLine(identifier, next, line);
  }
  async reconcileAssemblyDependenciesOnLoad(cart = null) {
    if (this._isReconcilingAssembly) return false;
    this._isReconcilingAssembly = true;
    try {
      const result = await reconcileAssemblyDependencies(
        cart,
        this.getSectionRequestPayload()
      );
      if (!result.changed) return false;
      let finalCart = result.cart;
      if (window.PackageProtection) {
        const protectionState = await window.PackageProtection.sync(finalCart);
        finalCart = (protectionState == null ? void 0 : protectionState.cart) || finalCart;
      }
      if (!this.hasRenderedSections(finalCart) || !this.renderCartState(finalCart.sections, {
        preserveOpenState: true,
        cart: finalCart,
        reconcileProtection: false
      })) {
        await this.refreshCartDrawer({
          preserveOpenState: true,
          cart: finalCart,
          reconcileProtection: false
        });
      }
      if (result.noticeRequired) {
        showAssemblyRemovalNotice(this);
      }
      return true;
    } catch (error) {
      console.error("Assembly dependency reconciliation error:", error);
      return false;
    } finally {
      this._isReconcilingAssembly = false;
    }
  }
  onAssemblyCartUpdated(event) {
    var _a;
    if (this._isReconcilingAssembly) return;
    const cart = (_a = event == null ? void 0 : event.detail) == null ? void 0 : _a.cart;
    if (!Array.isArray(cart == null ? void 0 : cart.items)) return;
    this.reconcileAssemblyDependenciesOnLoad(cart);
  }
  async updateCartLine(identifier, quantity, fallbackLine = null) {
    const validIdentifier = typeof identifier === "string" && identifier.length > 0 || Number.isInteger(identifier) && identifier > 0;
    const validQuantity = Number.isInteger(quantity) && quantity >= 0;
    if (!validIdentifier || !validQuantity) {
      this.setCartError("We couldn’t update this item. Please refresh and try again.");
      return false;
    }
    try {
      this.setUpdating(true);
      const change = typeof identifier === "string" ? { id: identifier, quantity } : { line: identifier, quantity };
      let res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...change,
          ...this.getSectionRequestPayload()
        })
      });
      const canRetryByLine = !res.ok && typeof identifier === "string" && Number.isInteger(fallbackLine) && fallbackLine > 0;
      if (canRetryByLine) {
        res = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            line: fallbackLine,
            quantity,
            ...this.getSectionRequestPayload()
          })
        });
      }
      if (!res.ok) throw new Error(`Cart update failed (${res.status})`);
      let cart = await res.json();
      let renderedSections = this.hasRenderedSections(cart) ? cart.sections : null;
      const assemblyResult = await reconcileAssemblyDependencies(
        cart,
        this.getSectionRequestPayload()
      );
      cart = assemblyResult.cart;
      if (this.hasRenderedSections(cart)) {
        renderedSections = cart.sections;
      } else if (assemblyResult.changed) {
        renderedSections = null;
      }
      let protectionState = null;
      if (window.PackageProtection) {
        protectionState = await window.PackageProtection.sync(cart);
      }
      const finalCart = (protectionState == null ? void 0 : protectionState.cart) || cart;
      const protectionChanged = !!(protectionState == null ? void 0 : protectionState.changed);
      const usedInlineSections = !protectionChanged && renderedSections && this.renderCartState(renderedSections, {
        preserveOpenState: true,
        cart: finalCart,
        reconcileProtection: false
      });
      if (!usedInlineSections) {
        await this.refreshCartDrawer({
          preserveOpenState: true,
          cart: finalCart
        });
      }
      if (assemblyResult.noticeRequired) {
        showAssemblyRemovalNotice(this);
      }
      if (protectionState == null ? void 0 : protectionState.error) {
        this.setCartError(
          "Cart updated, but package protection could not be synchronized. Please try again."
        );
        return false;
      }
      return true;
    } catch (err) {
      console.error("Cart update error:", err);
      try {
        await this.refreshCartDrawer({ preserveOpenState: true });
      } catch (refreshError) {
        console.error("Cart recovery refresh error:", refreshError);
      }
      this.setCartError("We couldn’t update your cart. Please try again.");
      return false;
    } finally {
      this.setUpdating(false);
    }
  }
  /* -------------------------------- */
  /* DRAWER */
  /* -------------------------------- */
  async refreshCartDrawer({
    open = true,
    preserveOpenState = false,
    cart = null,
    reconcileProtection = true
  } = {}) {
    if (this.hasRenderedSections(cart) && this.renderCartState(cart.sections, {
      open,
      preserveOpenState,
      cart,
      reconcileProtection
    })) {
      return;
    }
    const sectionIds = this.getSectionIdsToRender();
    const response = await fetch(`?sections=${sectionIds.join(",")}`, {
      headers: { "X-Requested-With": "XMLHttpRequest" }
    });
    if (!response.ok) {
      throw new Error(`Cart drawer refresh failed (${response.status})`);
    }
    const html = await response.json();
    if (!this.renderCartState(html, {
      open,
      preserveOpenState,
      cart,
      reconcileProtection
    })) {
      throw new Error("Cart drawer section was missing from the response");
    }
  }
  bindOutsideClick() {
    if (this._onOutsideClick) return;
    this._onOutsideClick = (e) => {
      var _a, _b, _c, _d, _e, _f;
      if (!this.classList.contains("active")) return;
      if ((_b = (_a = e.target) == null ? void 0 : _a.closest) == null ? void 0 : _b.call(_a, ".custom-popup-content")) return;
      if ((_d = (_c = e.target) == null ? void 0 : _c.closest) == null ? void 0 : _d.call(_c, ".custom-popup")) return;
      if (e.target && e.target.closest && e.target.closest("#CartDrawer-Overlay")) {
        this.close();
        return;
      }
      const drawer = this.querySelector("#CartDrawer");
      if (!drawer) return;
      if (drawer.contains(e.target)) return;
      if ((_f = (_e = e.target) == null ? void 0 : _e.closest) == null ? void 0 : _f.call(_e, ".cart-icon-bubble")) return;
      this.close();
    };
    document.addEventListener("click", this._onOutsideClick);
  }
  applySections(sectionsMap, { cart = null, reconcileProtection = true } = {}) {
    var _a;
    if (!sectionsMap || typeof sectionsMap !== "object") return false;
    this.stopTrustBarAutoplay();
    const html = sectionsMap["cart-drawer"];
    const shell = this.querySelector("#CartDrawer");
    if (!html || !shell) return false;
    const container = document.createElement("div");
    container.innerHTML = html;
    const renderedDrawer = container.querySelector("cart-drawer");
    const renderedShell = container.querySelector("#CartDrawer");
    if (!renderedShell) return false;
    shell.innerHTML = renderedShell.innerHTML;
    this.classList.toggle(
      "is-empty",
      !!(renderedDrawer == null ? void 0 : renderedDrawer.classList.contains("is-empty"))
    );
    this.initTrustBar();
    this.startTrustBarAutoplay();
    const protectionState = ((_a = window.PackageProtection) == null ? void 0 : _a.isCartSnapshot(cart)) ? window.PackageProtection.buildState(cart) : null;
    this.initProtectionToggle(protectionState);
    if (reconcileProtection) this.reconcileProtection(cart);
    return true;
  }
  updateProtectionUI(state) {
    const toggle = this.querySelector(".toggle-protection");
    const knob = toggle == null ? void 0 : toggle.querySelector("span");
    const priceEl = this.querySelector("[data-protection-price]");
    const price = (state == null ? void 0 : state.price) || (priceEl == null ? void 0 : priceEl.dataset.protectionFallbackPrice);
    if (priceEl && price) {
      priceEl.textContent = price;
    }
    if (!toggle) return;
    toggle.classList.toggle("active-toggle", !!(state == null ? void 0 : state.active));
    toggle.classList.toggle("bg-[#2ABC34]", !!(state == null ? void 0 : state.active));
    toggle.classList.toggle("bg-[#ccc]", !(state == null ? void 0 : state.active));
    if (knob) {
      knob.classList.toggle("translate-x-[16px]", !!(state == null ? void 0 : state.active));
    }
    this.updateProtectionStatusUI(state);
  }
  updateProtectionStatusUI(state) {
    const protectionCard = this.querySelector("#CartDrawer-Protection");
    const statusEl = this.querySelector("[data-protection-status]");
    const productImage = protectionCard == null ? void 0 : protectionCard.querySelector('img[alt]:not([alt=""])');
    const statusIcon = statusEl == null ? void 0 : statusEl.querySelector("img");
    const isActive = !!(state == null ? void 0 : state.active);
    if (protectionCard) {
      protectionCard.classList.toggle("border-[#50238A]", isActive);
      protectionCard.classList.toggle("bg-[#F6EEFF]", isActive);
      protectionCard.classList.toggle("border-[#E94141]", !isActive);
      protectionCard.classList.toggle("bg-[#FFF7F7]", !isActive);
    }
    if (productImage) {
      productImage.src = isActive ? "https://cdn.shopify.com/s/files/1/0559/6944/8111/files/Icon_26aee4ee-235e-4148-809a-549574b55c42.png?v=1771352407" : "https://cdn.shopify.com/s/files/1/0559/6944/8111/files/Icon_1_8fa3a023-7dd5-4223-8b04-20cdbdac3606.png?v=1771352407";
    }
    if (statusEl) {
      statusEl.textContent = isActive ? "Your package is protected." : "Your package is not protected.";
      if (statusIcon) {
        statusEl.append(" ");
        statusEl.appendChild(statusIcon);
      }
    }
  }
  /* -------------------------------- */
  /* PACKAGE PROTECTION UI (DELEGATE) */
  /* -------------------------------- */
  optimisticToggleUI(toggle) {
    toggle.classList.toggle("active-toggle");
    toggle.classList.toggle("bg-[#2ABC34]");
    toggle.classList.toggle("bg-[#ccc]");
    const knob = toggle.querySelector("span");
    if (knob) {
      knob.classList.toggle("translate-x-[16px]");
    }
  }
  initProtectionToggle(initialState = null) {
    const toggle = this.querySelector(".toggle-protection");
    if (!toggle || !window.PackageProtection) return;
    if (initialState) this.syncProtectionToggle(toggle, initialState);
    toggle.onclick = async () => {
      if (this._optimisticProtection) return;
      const wasActive = toggle.classList.contains("active-toggle");
      if (wasActive) this.setManualProtectionOptOut(true);
      else this.setManualProtectionOptOut(false);
      this._optimisticProtection = true;
      let confirmedState = null;
      try {
        this.optimisticToggleUI(toggle);
        this.setUpdating(true);
        toggle.classList.add("pointer-events-none", "opacity-70");
        confirmedState = await window.PackageProtection.toggle();
        await this.refreshCartDrawer({
          preserveOpenState: true,
          cart: confirmedState == null ? void 0 : confirmedState.cart
        });
      } catch (err) {
        console.error(err);
        if (toggle.classList.contains("active-toggle") !== wasActive) {
          this.optimisticToggleUI(toggle);
        }
      } finally {
        this._optimisticProtection = false;
        toggle.classList.remove("pointer-events-none", "opacity-70");
        this.setUpdating(false);
        if (confirmedState) {
          this.updateProtectionUI(confirmedState);
          this.updateProtectionPrice(confirmedState);
        } else if (window.PackageProtection) {
          window.PackageProtection.getState().then((state) => {
            this.updateProtectionUI(state);
            this.updateProtectionPrice(state);
          });
        }
      }
    };
  }
  updateProtectionPrice(state) {
    const priceEl = this.querySelector("[data-protection-price]");
    if (!priceEl) return;
    const price = (state == null ? void 0 : state.price) || priceEl.dataset.protectionFallbackPrice;
    if (price) priceEl.textContent = price;
  }
  syncProtectionToggle(toggle, state = null) {
    if (this._optimisticProtection) return;
    if (!window.PackageProtection) return;
    const knob = toggle.querySelector("span");
    const applyState = (state2) => {
      toggle.classList.toggle("active-toggle", state2.active);
      toggle.classList.toggle("bg-[#2ABC34]", state2.active);
      toggle.classList.toggle("bg-[#ccc]", !state2.active);
      if (knob) {
        knob.classList.toggle("translate-x-[16px]", state2.active);
      }
      const priceEl = this.querySelector("[data-protection-price]");
      if (priceEl && state2.price) {
        priceEl.textContent = state2.price;
      }
      this.updateProtectionStatusUI(state2);
    };
    if (state) applyState(state);
    else window.PackageProtection.getState().then(applyState);
  }
  /* -------------------------------- */
  /* ACCESSIBILITY */
  /* -------------------------------- */
  setHeaderCartIconAccessibility() {
    document.querySelectorAll(".cart-icon-bubble").forEach((link) => {
      link.setAttribute("role", "button");
      link.setAttribute("aria-haspopup", "dialog");
      link.addEventListener("click", (e) => {
        e.preventDefault();
        this.open(link);
      });
      link.addEventListener("keydown", (e) => {
        if (e.code === "Space") {
          e.preventDefault();
          this.open(link);
        }
      });
    });
  }
  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;
    requestAnimationFrame(() => {
      this.classList.add("active");
      this.startTrustBarAutoplay();
    });
    document.body.classList.add("overflow-hidden");
  }
  close() {
    this.closeActivePopup();
    this.stopTrustBarAutoplay();
    this.classList.remove("active");
    document.body.classList.remove("overflow-hidden");
  }
  openPopup(selector) {
    if (!selector) return;
    const popup = this.querySelector(selector) || document.querySelector(selector);
    if (!popup) return;
    popup.style.display = "flex";
    popup.setAttribute("aria-hidden", "false");
  }
  closePopup(popup) {
    if (!popup) return false;
    popup.style.display = "none";
    popup.setAttribute("aria-hidden", "true");
    return true;
  }
  closeActivePopup() {
    const popup = this.querySelector('.custom-popup[style*="display: flex"]');
    if (!popup) return false;
    return this.closePopup(popup);
  }
  renderContents(parsedState) {
    if (parsedState == null ? void 0 : parsedState.sections) {
      if (!this.applySections(parsedState.sections)) {
        this.refreshCartDrawer({ open: true });
        return;
      }
      this.dispatchCartUpdated();
    } else {
      this.refreshCartDrawer({ open: true });
      return;
    }
    this.open();
  }
  dispatchCartUpdated(cart = null) {
    window.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: cart && Array.isArray(cart.items) ? { cart } : {}
      })
    );
  }
  getSectionsToRender() {
    return [{ id: "cart-drawer" }];
  }
  setUpdating(state) {
    const items = this.querySelector("#CartDrawer-CartItems");
    items == null ? void 0 : items.classList.toggle("loading", !!state);
    this.setCheckoutDisabled(state);
  }
  setCheckoutDisabled(state) {
    const buttons = this.querySelectorAll(
      "#CartDrawer-Checkout, [data-checkout-without-protection]"
    );
    buttons.forEach((button) => {
      button.disabled = state;
      button.classList.toggle("opacity-50", state);
      button.classList.toggle("pointer-events-none", state);
    });
  }
  setQtyButtonLoading(button, state) {
    if (!button) return;
    button.classList.toggle("pointer-events-none", state);
    button.classList.toggle("opacity-50", state);
    let spinner = button.querySelector(".qty-spinner");
    if (state) {
      if (!spinner) {
        spinner = document.createElement("span");
        spinner.className = "qty-spinner absolute inset-0 flex items-center justify-center";
        spinner.innerHTML = `
          <svg class="w-4 h-4 animate-spin text-current" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10"
                    stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
        `;
        button.appendChild(spinner);
      }
    } else {
      spinner == null ? void 0 : spinner.remove();
    }
  }
  setActiveElement(el) {
    this.activeElement = el;
  }
}
if (!window.customElements.get("cart-drawer")) {
  window.customElements.define("cart-drawer", CartDrawer);
}
